import { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-yaml';
import 'prismjs/themes/prism-tomorrow.css';
import yaml from 'js-yaml';
import { Skeleton } from '../ui/Skeleton';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tenantCode: string | null;
}

export function TenantNotificationConfigDialog({ open, onClose, onSuccess, tenantCode }: Props) {
  const [yamlContent, setYamlContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [availableChannels, setAvailableChannels] = useState<string[]>([]);

  const effectiveTenant = useMemo(() => String(tenantCode || '').toLowerCase(), [tenantCode]);

  useEffect(() => {
    if (!open || !effectiveTenant) return;
    void loadConfig();
  }, [open, effectiveTenant]);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    setValidationError(null);
    try {
      // Load available channels from master config
      const channelsRes = await apiClient.getNotificationChannels();
      setAvailableChannels(Object.keys(channelsRes?.channels || {}));

      // Load tenant config
      const res = await apiClient.getNotificationsTenantConfig(effectiveTenant);
      const monitoring = res?.monitoring || {};
      
      // Extract notification-related parts from monitoring config
      const notificationConfig: any = {};
      
      // Grafana notifications
      if (monitoring?.grafana?.notifications) {
        notificationConfig.grafana = { notifications: monitoring.grafana.notifications };
      }
      
      // Alertmanager notifications
      if (monitoring?.alertmanager?.notifications) {
        notificationConfig.alertmanager = { notifications: monitoring.alertmanager.notifications };
      }
      
      // Jira notifications
      if (monitoring?.jira?.notifications) {
        notificationConfig.jira = { notifications: monitoring.jira.notifications };
      }
      
      // Services with notifications
      if (Array.isArray(monitoring?.services)) {
        const servicesWithNotifications = monitoring.services
          .filter((s: any) => s?.notifications)
          .map((s: any) => ({
            serviceId: s.serviceId,
            notifications: s.notifications,
          }));
        if (servicesWithNotifications.length > 0) {
          notificationConfig.services = servicesWithNotifications;
        }
      }

      const dumped = yaml.dump(notificationConfig, { noRefs: true, lineWidth: 160 });
      setYamlContent(dumped || '');
    } catch (e: any) {
      setError(e?.message || 'Failed to load tenant notification config');
    } finally {
      setLoading(false);
    }
  };

  const validateYaml = (content: string): boolean => {
    try {
      yaml.load(content);
      setValidationError(null);
      return true;
    } catch (err: any) {
      setValidationError(`Invalid YAML: ${err.message}`);
      return false;
    }
  };

  const highlightYaml = (code: string) => {
    return Prism.highlight(code, Prism.languages.yaml, 'yaml');
  };

  const handleSave = async () => {
    if (!validateYaml(yamlContent)) return;

    setSaving(true);
    setError(null);
    try {
      const parsed = (yaml.load(yamlContent) as any) || {};
      
      // Get current full monitoring config
      const currentRes = await apiClient.getNotificationsTenantConfig(effectiveTenant);
      const monitoring = { ...currentRes?.monitoring || {} };
      
      // Update grafana notifications
      if (parsed.grafana?.notifications) {
        monitoring.grafana = { ...monitoring.grafana, notifications: parsed.grafana.notifications };
      }
      
      // Update alertmanager notifications
      if (parsed.alertmanager?.notifications) {
        monitoring.alertmanager = { ...monitoring.alertmanager, notifications: parsed.alertmanager.notifications };
      }
      
      // Update jira notifications
      if (parsed.jira?.notifications) {
        monitoring.jira = { ...monitoring.jira, notifications: parsed.jira.notifications };
      }
      
      // Update services notifications
      if (Array.isArray(parsed.services)) {
        const serviceNotifications = new Map(
          parsed.services.map((s: any) => [s.serviceId, s.notifications])
        );
        
        if (Array.isArray(monitoring.services)) {
          monitoring.services = monitoring.services.map((s: any) => {
            if (serviceNotifications.has(s.serviceId)) {
              return { ...s, notifications: serviceNotifications.get(s.serviceId) };
            }
            return s;
          });
        }
      }

      await apiClient.updateNotificationsTenantConfig(effectiveTenant, { monitoring });
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save tenant notification config');
    } finally {
      setSaving(false);
    }
  };

  const onValueChange = (v: string) => {
    setYamlContent(v);
    setValidationError(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent maxWidth="4xl">
        <DialogHeader>
          <DialogTitle>Edit Tenant Notification Config: {effectiveTenant.toUpperCase()}</DialogTitle>
          <DialogDescription>
            Configure notification settings for Grafana, Alertmanager, Jira, and Services.
            {availableChannels.length > 0 && (
              <span className="block mt-1 text-xs">
                Available channels: {availableChannels.join(', ')}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {loading && (
            <div className="space-y-2 py-4">
              <Skeleton className="h-64 w-full" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200 mb-4">
              {error}
            </div>
          )}

          {validationError && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-amber-800 dark:text-amber-200 mb-4">
              {validationError}
            </div>
          )}

          {!loading && (
            <div className="relative">
              <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700 focus-within:border-blue-500">
                <div className="max-h-[500px] overflow-auto custom-scrollbar">
                  <Editor
                    value={yamlContent}
                    onValueChange={onValueChange}
                    highlight={highlightYaml}
                    padding={16}
                    style={{
                      fontFamily: '"Fira Code", "Fira Mono", monospace',
                      fontSize: 14,
                      backgroundColor: '#1e293b',
                      color: '#e2e8f0',
                      minHeight: '300px',
                    }}
                    className="outline-none"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Edit notification channels for each monitoring source. Use channel names defined in master config.
              </p>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button onClick={onClose} disabled={saving} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading || !!validationError}>
            {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
